/**
 * Water Resource — per-type preferences sub-form (Workflow Prompt 2).
 *
 * SSOT — `coolingType` lived here pre-SSOT but duplicated
 * `SiteEntity.projectIntent.coolingStrategy`. It was deleted from
 * `WaterResourcePreferences` in SSOT-3; this form now collects only
 * report-type-only Water Resource fields:
 *   - waterRightsKnown
 *   - droughtScenario
 *   - wastewaterTarget
 *
 * Pack: docs/prompt-packs/Site_SSOT_Prompt.md
 *
 * No required fields after SSOT — the form is fully optional. Reports
 * still render when nothing is set; the wizard's missing-intent banner
 * (SSOT-9) signals when cooling strategy hasn't been picked on the site.
 */
import React from 'react';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import type { WaterResourcePreferences } from '../../../types/siteRag';
import { FieldRow } from './shared';
import { validateWaterResourcePreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validateWaterResourcePreferences };

interface Props {
  value: WaterResourcePreferences | undefined;
  onChange: (next: WaterResourcePreferences) => void;
  /**
   * SSOT — opens the Site Project Intent editor when the user wants to
   * edit the canonical cooling-strategy field. Optional; see
   * FeasibilityPreferences.tsx for parity rationale.
   */
  onOpenSiteIntent?: () => void;
}

export const WaterResourcePreferencesForm: React.FC<Props> = ({ value: rawValue, onChange, onOpenSiteIntent }) => {
  const value: WaterResourcePreferences = (rawValue ?? {}) as WaterResourcePreferences;
  const set = <K extends keyof WaterResourcePreferences>(key: K, v: WaterResourcePreferences[K]) => {
    onChange({ ...value, [key]: v });
  };

  return (
    <div className="space-y-5">
      {/* SSOT — shared inputs live on Site Project Intent. */}
      <div
        data-testid="ssot-summary-water-resource"
        className="text-[10px] text-text-muted bg-state-hover border border-border px-3 py-2"
      >
        Cooling strategy is set at <strong>Site Project Intent</strong>.{onOpenSiteIntent ? (
          <>{' '}
            <button
              type="button"
              onClick={onOpenSiteIntent}
              className="text-primary underline"
              data-testid="ssot-open-site-intent-water-resource"
            >
              Edit Site Project Intent →
            </button>
          </>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FieldRow label="Drought scenario" fieldKey="droughtScenario" help="Which return-period drought to stress-test against. 1-in-100 is more conservative.">
          <Select value={value.droughtScenario ?? ''} onValueChange={(v) => set('droughtScenario', v as WaterResourcePreferences['droughtScenario'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1-in-10">1-in-10 year</SelectItem>
              <SelectItem value="1-in-100">1-in-100 year</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Wastewater target" fieldKey="wastewaterTarget" help="NPDES = standard regulated discharge. ZLD = zero liquid discharge. Reuse = on-site reuse.">
          <Select value={value.wastewaterTarget ?? ''} onValueChange={(v) => set('wastewaterTarget', v as WaterResourcePreferences['wastewaterTarget'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="npdes">NPDES</SelectItem>
              <SelectItem value="zld">ZLD</SelectItem>
              <SelectItem value="reuse">Reuse</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between" data-testid="pref-field-waterRightsKnown">
          <div>
            <Label className="type-section text-text-secondary">Water rights known</Label>
            <div className="text-[10px] text-text-muted">
              You have certainty about the water rights doctrine + appropriation. Skips the deep rights analysis.
            </div>
          </div>
          <Switch
            checked={!!value.waterRightsKnown}
            onCheckedChange={(b) => set('waterRightsKnown', b)}
          />
        </div>
      </div>
    </div>
  );
};
